import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import CheckIcon from '@/Components/icons/checkmark.svg?react'
import { cn } from '@/lib/utils'

const chipVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-full border border-border-default transition-all duration-200 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-background-surface text-text-primary hover:bg-background-hover',
        selected: 'bg-button-primary-selected-segment text-text-primary',
      },
      size: {
        default: 'h-8 md:h-9 px-4 py-2 text-sm',
        sm: 'h-7 px-3 py-1 text-xs',
        lg: 'h-11 px-5 py-2.5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLButtonElement>, 'onChange' | 'onSelect'>,
    VariantProps<typeof chipVariants> {
  selected?: boolean
  onSelect?: (selected: boolean) => void
  icon?: React.ReactNode
  disabled?: boolean
}

const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, variant: _variant, size, selected = false, onSelect, icon, disabled = false, children, ...props }, ref) => {
    const handleClick = () => {
      if (disabled) return
      onSelect?.(!selected)
    }

    return (
      <button
        ref={ref}
        type='button'
        className={cn(
          chipVariants({
            variant: selected ? 'selected' : 'default',
            size,
            className,
          }),
          disabled && 'cursor-not-allowed opacity-50'
        )}
        onClick={handleClick}
        disabled={disabled}
        {...props}
      >
        {icon && <div className='flex-shrink-0'>{icon}</div>}
        {children && <span className='whitespace-nowrap'>{children}</span>}
        {selected && <CheckIcon className='text-text-secondary size-4' />}
      </button>
    )
  }
)

Chip.displayName = 'Chip'

export { Chip, chipVariants }
